import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
} from "@tanstack/react-table";
import PatientListTable from "./PatientListTable";
import PatientListFooter from "./PatientListFooter";
import PatientListViews from "./PatientListViews";
import { makeColumns } from "./patientListColumns";
import { setDownloadProperties } from "./actions";
import { acquirePlanLock } from "../../helpers/backend_api";
import {
    filterPatientsByView,
    getPlanDownloadInfo,
    PATIENT_LIST_VIEWS,
} from "./helpers";

function PatientList({ patientsFetchedData, loadComplete, checkNotification }) {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.LoginReducer.authToken);
    const useruuid = useSelector((state) => state.LoginReducer.useruuid);

    const [sorting, setSorting] = useState([
        { id: "last_modified_date", desc: true },
    ]);
    const [activeView, setActiveView] = useState(PATIENT_LIST_VIEWS[0].id);
    const tableScrollRef = useRef(null);

    const handleDownloadPlan = useCallback(
        (plan) => {
            const { pdfOnly } = getPlanDownloadInfo(plan);
            dispatch(
                setDownloadProperties(
                    true,
                    plan?.MetaData?.PatientUUID,
                    plan?.MetaData?.PlanUUID,
                    pdfOnly
                )
            );
        },
        [dispatch]
    );

    const handleAccessPlan = useCallback(
        (plan) => {
            const planuuid = plan?.MetaData?.PlanUUID;
            checkNotification({
                planUuid: planuuid,
                patientUuid: plan?.MetaData?.PatientUUID,
                visitUuid: plan?.MetaData?.VisitUUID,
                SavedData: plan?.SavedData,
            });
            acquirePlanLock({ planuuid, useruuid, token });
        },
        [checkNotification, useruuid, token]
    );

    const filteredData = useMemo(
        () => filterPatientsByView(patientsFetchedData, activeView),
        [patientsFetchedData, activeView]
    );

    const columns = useMemo(
        () =>
            makeColumns({
                useruuid,
                onAccessPlan: handleAccessPlan,
                onDownloadPlan: handleDownloadPlan,
            }),
        [useruuid, handleAccessPlan, handleDownloadPlan]
    );

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        state: {
            sorting,
        },
    });

    const handleViewChange = (viewId) => {
        setActiveView(viewId);
        tableScrollRef.current?.scrollTo({ top: 0 });
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-col gap-y-3 px-35 pt-2">
            <PatientListViews
                activeView={activeView}
                onViewChange={handleViewChange}
            />
            <PatientListTable
                table={table}
                columns={columns}
                loadComplete={loadComplete}
                scrollRef={tableScrollRef}
            />
            <PatientListFooter table={table} scrollRef={tableScrollRef} />
        </div>
    );
}

export default withRouter(PatientList);
